import Prompt from '@components/Prompt'
import { useUnstakeButton } from '@plug-core/hooks/useStaking'
import { StakeButtonProps } from '@plug-core/interfaces/staking'
import { SwapProvider } from '@plug-core/providers/SwapProvider'
import { Button, Heading, Text, cn } from '@ui'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { HiInformationCircle } from 'react-icons/hi'

const UnstakeButton: FC<StakeButtonProps> = ({ isLoading, ...props }) => {
  const { t } = useTranslation()

  const {
    setUnstakeMode,
    unstakeMode,
    onInfoClick,
    userError,
    reviewStake,
    setPrompt,
    prompt,
    delayedAmount,
    immediateAmount,
  } = useUnstakeButton(props)

  const optionClass = (selected: boolean) =>
    cn(
      'flex flex-1 cursor-pointer flex-col rounded-sm border border-solid bg-surface p-2.5 transition-colors duration-150 hover:bg-surface-2',
      selected ? 'border-fg' : 'border-transparent'
    )

  return (
    <div className="flex flex-col">
      <div className="mb-4 flex gap-3">
        <div
          onClick={() => setUnstakeMode('immediate')}
          className={optionClass(unstakeMode === 'immediate')}
        >
          <div className="flex items-center justify-between">
            <Heading level={5} className="text-[16px]">
              {t('stake.unstake.immediate.short')}
            </Heading>

            <HiInformationCircle onClick={(e) => onInfoClick(e, 'immediate')} size={20} />
          </div>

          <Text variant="label" weight="regular" tone="fg-2" tabular className="mt-0.5">
            {t('stake.unstake.receive')} {immediateAmount}
          </Text>
        </div>

        <div
          onClick={() => setUnstakeMode('delayed')}
          className={optionClass(unstakeMode === 'delayed')}
        >
          <div className="flex items-center justify-between">
            <Heading level={5} className="text-[16px]">
              {t('stake.unstake.delayed.short')}
            </Heading>

            <HiInformationCircle onClick={(e) => onInfoClick(e, 'delayed')} size={20} />
          </div>

          <Text variant="label" weight="regular" tone="fg-2" tabular className="mt-0.5">
            {t('stake.unstake.receive')} {delayedAmount}
          </Text>
        </div>
      </div>

      <Button
        variant="primary"
        disabled={!!userError || isLoading}
        onClick={reviewStake}
        fullWidth={true}
      >
        {userError || t('stake.unstakeNicp')}
      </Button>

      <Prompt show={!!prompt} close={() => setPrompt(null)}>
        <div className="flex flex-col items-center gap-2">
          <Heading level={4} align="center">
            {prompt?.title}
          </Heading>
          <Text align="center" tone="fg-2">
            {prompt?.description}
          </Text>
        </div>

        <Button onClick={() => setPrompt(null)} variant="secondary">
          {t('common.understood')}
        </Button>
      </Prompt>
    </div>
  )
}

const UnstakeButtonWrapped = (props: StakeButtonProps) => (
  <SwapProvider>
    <UnstakeButton {...props} />
  </SwapProvider>
)

export { UnstakeButtonWrapped as UnstakeButton }
